// Global API logger utility
class ApiLogger {
  constructor() {
    this.isInitialized = false
    this.originalFetch = null
  }

  initialize() {
    if (this.isInitialized) return

    try {
      // Check if fetch has already been wrapped
      if (window.fetch.toString().includes('apiLogger')) return

      // Store original fetch with proper binding
      const originalFetch = window.fetch._original || window.fetch
      this.originalFetch = originalFetch.bind(window)
      
      // Create wrapped fetch function
      const self = this
      window.fetch = async function(...args) {
        const startTime = performance.now()
        const [url, options = {}] = args
        
        // Skip logging for certain URLs
        const urlString = typeof url === 'string' ? url : url.toString()
        if (urlString.includes('hot-update') || 
            urlString.includes('node_modules') ||
            urlString.includes('vite') ||
            urlString.includes('@vite') ||
            urlString.includes('/rest/v1/api_logs')) { // Don't log our own logging calls
          return originalFetch.call(window, ...args)
        }
        
        let requestBody = null
        if (options.body) {
          try {
            requestBody = typeof options.body === 'string' ? options.body : JSON.stringify(options.body)
            // Truncate if too large
            if (requestBody.length > 10000) {
              requestBody = requestBody.substring(0, 10000) + '... [truncated]'
            }
          } catch (e) {
            requestBody = '[Unable to parse]'
          }
        }
        
        const logEntry = {
          id: Date.now() + Math.random(),
          timestamp: new Date().toISOString(),
          method: options.method || 'GET',
          endpoint: urlString,
          requestHeaders: options.headers || {},
          requestBody: requestBody,
          status: null,
          responseHeaders: {},
          responseBody: null,
          duration: 0,
          type: urlString.includes('devrev.ai') ? 'DevRev' : 
                urlString.includes('supabase.co') ? 'Supabase' : 
                urlString.includes('/rest/v1/') ? 'Supabase' : 'API'
        }
        
        
        try {
          const response = await originalFetch.call(window, ...args)
          const endTime = performance.now()
          
          // Clone response to read body without consuming it
          const responseClone = response.clone()
          let responseBody = null
          
          // Log asynchronously to avoid blocking the response
          setTimeout(async () => {
            try {
              const contentType = response.headers.get('content-type')
              if (contentType && contentType.includes('application/json')) {
                const jsonData = await responseClone.json()
                const jsonString = JSON.stringify(jsonData, null, 2)
                // Truncate if too large
                responseBody = jsonString.length > 20000 
                  ? jsonString.substring(0, 20000) + '\n... [truncated - response too large]'
                  : jsonData
              } else {
                const textData = await responseClone.text()
                responseBody = textData.length > 10000 
                  ? textData.substring(0, 10000) + '... [truncated]'
                  : textData
              }
            } catch (e) {
              responseBody = '[Unable to parse response]'
            }
            
            // Update log entry
            logEntry.status = response.status
            logEntry.duration = Math.round(endTime - startTime)
            logEntry.responseHeaders = Object.fromEntries(response.headers.entries())
            logEntry.responseBody = responseBody
            
            // Store in sessionStorage
            self.storeLog(logEntry)
          }, 0)
          
          return response
        } catch (error) {
          const endTime = performance.now()
          logEntry.status = 0
          logEntry.duration = Math.round(endTime - startTime)
          logEntry.responseBody = error.message
          
          // Store error log (but don't let logging errors break the app)
          try {
            self.storeLog(logEntry)
          } catch (e) {
            console.error('Failed to store error log:', e)
          }
          
          // Always rethrow the original error
          throw error
        }
      }
      
      this.isInitialized = true
    } catch (error) {
      console.error('❌ API Logger initialization failed:', error)
      // Don't let logger errors break the app
    }
  }

  storeLog(logEntry) {
    try {
      const existingLogs = JSON.parse(sessionStorage.getItem('apiLogs') || '[]')
      const updatedLogs = [logEntry, ...existingLogs].slice(0, 200) // Keep last 200
      sessionStorage.setItem('apiLogs', JSON.stringify(updatedLogs))
      
      // Save only meaningful DevRev API calls to Supabase database
      // Exclude internal telemetry/logging calls
      if (logEntry.type === 'DevRev') {
        if (!logEntry.endpoint.includes('ingestion') &&
            !logEntry.endpoint.includes('logData') &&
            !logEntry.endpoint.includes('observability')) {
          this.saveToSupabase(logEntry)
        }
      }
      
      // Dispatch event for components listening to log updates
      window.dispatchEvent(new CustomEvent('apiLogUpdated', {
        detail: {
          ...logEntry,
          componentId: 'apiLogs'
        }
      }))
    } catch (e) {
      console.error('Error storing API log:', e)
    }
  }
  
  async saveToSupabase(logEntry) {
    try {
      // Dynamically import supabase to avoid circular dependencies
      const { supabase } = await import('../lib/supabase')
      const { useAuth } = await import('../contexts/AuthContext')
      
      // Get current user
      const user = JSON.parse(localStorage.getItem('supabase.auth.user') || '{}')
      
      // Prepare log data for database
      const logData = {
        user_id: user?.id || null,
        user_email: user?.email || null,
        ip_address: 'client', // Client-side doesn't have real IP
        user_agent: navigator.userAgent,
        method: logEntry.method,
        endpoint: logEntry.endpoint,
        query_params: null, // Can parse from URL if needed
        request_headers: logEntry.requestHeaders,
        request_body: logEntry.requestBody,
        response_status: logEntry.status,
        response_headers: logEntry.responseHeaders,
        response_body: typeof logEntry.responseBody === 'string' 
          ? logEntry.responseBody 
          : JSON.stringify(logEntry.responseBody),
        response_time_ms: logEntry.duration,
        api_type: logEntry.type
      }
      
      // Insert into Supabase (fire and forget - don't block)
      supabase
        .from('api_logs')
        .insert([logData])
        .then(({ error }) => {
          if (error) {
            // Silent fail - don't log errors
          }
        })
    } catch (e) {
      // Silently fail - don't let logging errors break the app
    }
  }

  getLogs() {
    try {
      return JSON.parse(sessionStorage.getItem('apiLogs') || '[]')
    } catch (e) {
      console.error('Error getting logs:', e)
      return []
    }
  }

  clearLogs() {
    sessionStorage.removeItem('apiLogs')
    window.dispatchEvent(new CustomEvent('apiLogsCleared', {
      detail: { componentId: 'apiLogs' }
    }))
  }

  cleanup() {
    if (this.isInitialized && this.originalFetch) {
      window.fetch = this.originalFetch
      this.isInitialized = false
    }
  }
}

// Create singleton instance
export const apiLogger = new ApiLogger()

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  apiLogger.initialize()
}